import { SystemStyleFunction } from "@chakra-ui/theme-tools";

type StyleProps = Parameters<SystemStyleFunction>[0];

const theme = {
  styles: {
    global: {
      body: {
        overflowX: "hidden",
        backgroundColor: "bright",
        fontWeight: "500",
        color: "dark.500",
      },
      "html, body, #root": {
        height: "full",
        fontSize: {
          base: "14px",
          lg: "16px",
        },
      },
      "*:focus": {
        boxShadow: "none !important",
      },
      "*": {
        webkitTapHighlightColor: "transparent",
      },
    },
  },
  fonts: {
    heading: "Montserrat",
    body: "Montserrat",
  },
  colors: {
    primary: {
      400: "#AB434B",
      500: "#AB434B",
      600: "#963B42",
      700: "#963B42",
    },
    secondary: {
      400: "#F2AF4A",
      500: "#F2AF4A",
      600: "#D69A40",
      700: "#D69A40",
    },
    dark: {
      400: "#484250",
      500: "#36313E",
      600: "#2F2A36",
    },
    bright: {
      400: "#FBDECE",
      500: "#FBE3D3",
      600: "EDD6C7",
    },
  },
  components: {
    Container: {
      baseStyle: {
        maxW: "container.sm",
        px: "6",
      },
    },
    Button: {
      baseStyle: {
        borderRadius: "none",
      },
      variants: {
        solid: (props: StyleProps) => {
          if (props.colorScheme === "secondary") {
            return { color: "dark.500" };
          } else if (props.colorScheme === "dark") {
            return { bg: "dark.400", color: "bright.400" };
          }
        },
        outline: {
          border: "2px solid",
          color: "dark.500",
          borderColor: "dark.500",
          _active: {
            color: "primary.500",
            borderColor: "primary.500",
          },
          _hover: {
            color: "primary.500",
            borderColor: "primary.500",
          },
          _disabled: {
            color: "dark.500",
            pointerEvents: "none",
          },
        },
        checked: {
          color: "dark",
          bg: "bright.500",
          border: "1px solid",
          borderColor: "currentColor",
        },
        link: {
          color: "dark",
          _active: {
            color: "primary.500",
          },
          _hover: {
            color: "primary.500",
            textDecoration: "none",
          },
        },
      },
      sizes: {
        sm: {
          px: 5,
          height: 10,
          minW: 10,
        },
        md: {
          px: 8,
          height: 12,
          minW: 12,
        },
        lg: {
          h: 14,
          px: 12,
          minW: 12,
          fontSize: "md",
        },
      },
      defaultProps: {
        colorScheme: "primary",
      },
    },
    CloseButton: {
      baseStyle: {
        mt: "1",
        borderRadius: "none",
        _hover: {
          bg: "dark.600",
        },
      },
    },
    Input: {
      sizes: {
        lg: {
          field: {
            px: 4,
            borderRadius: "none",
          },
        },
      },
      variants: {
        outline: {
          field: {
            borderColor: "dark.400",
            border: "2px solid",
            _hover: {
              borderColor: "dark.400",
            },
          },
        },
      },
    },
    Link: {
      baseStyle: {
        color: "dark",
        _hover: {
          color: "primary.500",
        },
      },
    },
    Modal: {
      baseStyle: {
        dialog: {
          borderRadius: "none",
          boxShadow: "none",
          bg: "dark.500",
          color: "bright.400",
          mx: 2,
        },
        body: {
          py: 6,
        },
      },
      defaultProps: {
        isCentered: true,
      },
    },
    Divider: {
      baseStyle: (props: StyleProps) => ({
        borderColor: props.colorScheme === "bright" ? "bright.500" : "dark.500",
        opacity: props.colorScheme === "bright" ? 0.2 : 0.1,
        borderBottomWidth: "2px",
      }),
    },
    Tooltip: {
      baseStyle: {
        bg: "dark.400",
        color: "bright.400",
      },
    },
  },
};

export default theme;
